import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { Person as PersonIcon, Business as BusinessIcon, AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import CandidateAuth from './CandidateAuth';
import CompanyAuth from './CompanyAuth';
import AdminLogin from './AdminLogin';

const cardData = [
  {
    key: 'candidate',
    icon: <PersonIcon sx={{ fontSize: 32 }} />,
    iconClass: 'teal',
    title: 'Candidate',
    desc: 'Find your dream job with ease',
    login: 'Login',
    signup: 'Sign Up',
  },
  {
    key: 'company',
    icon: <BusinessIcon sx={{ fontSize: 32 }} />,
    iconClass: 'teal',
    title: 'Company',
    desc: 'Find perfect candidates for your team',
    login: 'Login',
    signup: 'Sign Up',
  },
  {
    key: 'admin',
    icon: <AdminIcon sx={{ fontSize: 32 }} />,
    iconClass: 'amber',
    title: 'Admin',
    desc: 'Manage the platform effectively',
    login: 'Login',
    signup: null,
  },
];

export default function WelcomePage({ onLoginSuccess }) {
  const [modal, setModal] = useState(null);
  const [modalType, setModalType] = useState('login');
  const navigate = useNavigate();

  const handleAuthSuccess = () => {
    setModal(null);
    setTimeout(() => {
      const user = JSON.parse(localStorage.getItem('gogig_user') || '{}');
      if (user.type === 'company' || user.type === 'candidate') {
        if (onLoginSuccess) onLoginSuccess();
        navigate(user.type === 'company' ? '/company/dashboard' : '/candidate/dashboard');
      } else {
        if (localStorage.getItem('gogig_admin')) {
          if (onLoginSuccess) onLoginSuccess();
          navigate('/admin/dashboard');
        }
      }
    }, 100);
  };

  const handleAdminLogin = () => {
    setModal(null);
    setTimeout(() => {
      if (onLoginSuccess) onLoginSuccess();
      navigate('/admin/dashboard');
    }, 100);
  };

  return (
    <Box className="welcome-page">
      <div className="welcome-bg-blur welcome-bg-blur-1" />
      <div className="welcome-bg-blur welcome-bg-blur-2" />

      <nav className="welcome-nav">
        <div className="welcome-nav-logo">
          <img src="/logo512.png" alt="GoGig" />
          <span>GoGig</span>
        </div>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" sx={{ color: '#94a3b8', borderColor: 'rgba(148,163,184,0.5)', fontWeight: 600, '&:hover': { borderColor: '#2dd4bf', color: '#2dd4bf' } }}>
            Contact Us
          </Button>
          <Button variant="contained" sx={{ background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)', fontWeight: 600, '&:hover': { background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)' } }}>
            About Us
          </Button>
        </Box>
      </nav>

      <section className="welcome-hero">
        <h1>
          Welcome to <span className="gradient-text">GoGig</span>
        </h1>
        <p>Your gateway to connecting talent with opportunities — choose your path to get started.</p>
      </section>

      <div className="welcome-cards">
        {cardData.map((card) => (
          <div key={card.key} className="welcome-card">
            <div className={`welcome-card-icon ${card.iconClass}`}>{card.icon}</div>
            <h2>{card.title}</h2>
            <p>{card.desc}</p>
            <div className="card-actions">
              <Button
                fullWidth
                variant="contained"
                onClick={() => { setModal(card.key); setModalType('login'); }}
                sx={{
                  background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  py: 1.25,
                  borderRadius: 2,
                  boxShadow: '0 4px 14px rgba(13, 148, 136, 0.35)',
                  '&:hover': { background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)', boxShadow: '0 6px 20px rgba(13, 148, 136, 0.4)' },
                }}
              >
                {card.login}
              </Button>
              {card.signup && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => { setModal(card.key); setModalType('signup'); }}
                  sx={{
                    borderColor: '#0d9488',
                    color: '#0d9488',
                    fontWeight: 600,
                    py: 1.25,
                    borderRadius: 2,
                    '&:hover': { borderColor: '#0f766e', color: '#0f766e', bgcolor: 'rgba(13, 148, 136, 0.08)' },
                  }}
                >
                  {card.signup}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div
          className="welcome-modal-backdrop"
          onClick={() => setModal(null)}
          role="presentation"
        >
          <div
            className="welcome-modal-paper"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {modal === 'candidate' ? (
              <CandidateAuth onClose={() => setModal(null)} onAuthSuccess={handleAuthSuccess} defaultStep={modalType} />
            ) : modal === 'company' ? (
              <CompanyAuth onClose={() => setModal(null)} onAuthSuccess={handleAuthSuccess} defaultStep={modalType} />
            ) : (
              <AdminLogin onLogin={handleAdminLogin} />
            )}
          </div>
        </div>
      )}

      <footer className="welcome-footer">
        <Link to="/pages/page_privacy">Privacy Policy</Link>
      </footer>
    </Box>
  );
}
